import { useDialogContext } from "@ariakit/react";
import { useLocation } from "wouter";
import { Army } from "../../domain/army";
import { DEFAULT_THEME, type ThemeId } from "../../domain/theme";
import { ArmyData } from "../../lib/db/types";
import { atomic } from "../atomic.css";
import { useDatabase } from "../database-provider";
import { FormDialog } from "../form-dialog";
import { FormField } from "../form-field";
import { useForm } from "../hooks/use-form";
import { useRulesets } from "../ruleset-provider/context";
import { ThemeField } from "../theme-field";

type FormValues = Pick<ArmyData, "name" | "description"> & {
  theme: ThemeId;
};

export function ArmyDialog(props: {
  initialValues: FormValues;
  onSubmit: (values: FormValues) => Promise<void>;
  submitLabel: string;
}) {
  const form = useForm({
    onSubmit(values) {
      return props.onSubmit({
        name: values.name.trim(),
        description: values.description.trim(),
        theme: values.theme,
      });
    },
    defaultValues: props.initialValues,
  });

  return (
    <FormDialog submitLabel={props.submitLabel} form={form} heading="Army">
      <FormField
        name={form.names.name}
        label="Name"
        placeholder="E.g. Th’ulex Prime Defense Force"
        autoFocusOnWide
      />

      <FormField
        name={form.names.description}
        label="Description"
        placeholder="Optionally add a backstory, how to play, etc..."
        render={<textarea rows={5} className={atomic({ resize: "none" })} />}
      />

      <ThemeField name={form.names.theme} />
    </FormDialog>
  );
}

export function CreateArmyDialog() {
  const rulesets = useRulesets();
  const [_, navigate] = useLocation();

  return (
    <ArmyDialog
      submitLabel="Add"
      initialValues={{
        name: "",
        description: "",
        theme: DEFAULT_THEME,
      }}
      onSubmit={async (values) => {
        const id = await rulesets.latest.createArmy(values);
        navigate(`~/army/a/${id}`);
      }}
    />
  );
}

export function UpdateArmyDialog(props: { army: Army }) {
  const { army } = props;
  const db = useDatabase();
  const store = useDialogContext();

  return (
    <ArmyDialog
      initialValues={{
        name: army.data.name,
        description: army.data.description,
        theme: army.theme,
      }}
      submitLabel="Save"
      onSubmit={async (values) => {
        await db.updateArmy({
          ...army.data,
          ...values,
        });

        store?.hide();
      }}
    />
  );
}
